import React, {
  useState,
  createContext,
  useContext,
  useEffect,
  useCallback,
} from 'react';
import PropTypes from 'prop-types';

const LoggerContext = createContext(null);
const { Provider } = LoggerContext;

export const useLogger = () => useContext(LoggerContext);

const LoggerProvider = ({ children, api, service, id }) => {
  const [state, setState] = useState({ errors: [], warns: [], infos: [] });
  const withReviewId = useCallback(
    log => ({ ...log, details: { ...log.details, review_id: id } }),
    [id]
  );

  const error = useCallback(
    log => {
      setState(state => ({ ...state, errors: [...state.errors, log] }));
      if (api?.isEnabled()) api.log(withReviewId({ ...log, level: 'error' }));
    },
    [api, withReviewId]
  );

  const warn = useCallback(
    log => {
      setState(state => ({ ...state, warns: [...state.warns, log] }));
      if (api?.isEnabled()) api.log(withReviewId({ ...log, level: 'warning' }));
    },
    [api, withReviewId]
  );

  const info = useCallback(
    log => {
      setState(state => ({ ...state, infos: [...state.infos, log] }));
      if (api?.isEnabled()) api.log(withReviewId({ ...log, level: 'info' }));
    },
    [api, withReviewId]
  );

  useEffect(() => {
    if (service) service.setServiceImplementation({ error, warn, info });
  }, [service, error, warn, info]);
  return <Provider value={{ state, error, warn, info }}>{children}</Provider>;
};

export const withLogger = Component => {
  return function WrappedComponent(props) {
    const { error, warn, info } = useLogger();
    return <Component {...props} logger={{ error, warn, info }} />;
  };
};
LoggerProvider.defaultProps = { service: null };
LoggerProvider.propTypes = {
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  service: PropTypes.shape({
    setServiceImplementation: PropTypes.func,
  }),
  id: PropTypes.string,
};

export default LoggerProvider;
export const LogConsumer = LoggerContext.Consumer;
